import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Construction, ArrowLeft, Home } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getAllModules } from '../../config/navigation';

/**
 * MODULE PLACEHOLDER COMPONENT
 * Shown for modules that are not yet implemented
 * 
 * Features:
 * - Shows module name from navigation.js
 * - Shows section
 * - Shows dependencies
 * - Shows development phase
 * - Provides navigation back
 */

const ModulePlaceholder = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Find module info from navigation.js based on current path
  const allModules = getAllModules();
  const currentModule = allModules.find(m => m.path === location.pathname);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
      <Card className="max-w-2xl w-full">
        <CardContent className="pt-12 pb-12 text-center">
          {/* Construction Icon */}
          <div className="flex justify-center mb-6">
            <div className="p-6 bg-[#FFF8E1] rounded-full">
              <Construction className="h-16 w-16 text-[#FFC107]" />
            </div>
          </div>

          {/* Module Info */}
          {currentModule ? (
            <>
              <h1 className="text-3xl font-bold text-[#2B2D42] mb-2">
                {currentModule.label}
              </h1>
              <p className="text-lg text-[#6C757D] mb-4">
                {currentModule.section} · {currentModule.phase || 'Upcoming'}
              </p>
              <p className="text-[#6C757D] mb-6 max-w-md mx-auto">
                {currentModule.description || 'This module is under development and will be available soon.'}
              </p>

              {/* Dependencies */}
              {currentModule.dependencies && currentModule.dependencies.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm text-[#6C757D] mb-2">Dependencies:</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {currentModule.dependencies.map((dep, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                      >
                        {dep}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Flags */}
              {currentModule.flags && currentModule.flags.length > 0 && (
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2 justify-center">
                    {currentModule.flags.map((flag, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded-full"
                      >
                        {flag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-[#2B2D42] mb-2">
                Module Under Development
              </h1>
              <p className="text-lg text-[#6C757D] mb-6">
                This feature is coming soon.
              </p>
            </>
          )}

          {/* Status Badge */}
          <div className="inline-flex items-center px-4 py-2 bg-[#FFC107] bg-opacity-20 rounded-lg mb-8">
            <span className="text-[#FFC107] font-semibold">Status: In Development</span>
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-4 justify-center">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Go Back
            </Button>
            <Button
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center gap-2 bg-[#00B4D8] hover:bg-[#0096B8]"
            >
              <Home className="h-4 w-4" />
              Dashboard
            </Button>
          </div>

          {/* Footer Info */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-sm text-[#ADB5BD]">
              Grupo BMP Platform · Phase P2 in Progress
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModulePlaceholder;
